// src/VendorManagement.js
// Vendor Management Page - CRUD operations for vendors

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import './styles.css';

export default function VendorManagement() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contactPerson: '',
    phone: '',
    address: '',
    specialization: '',
    rating: 3
  });

  // Fetch vendors on mount
  useEffect(() => {
    if (userProfile?.organizationId) {
      fetchVendors();
    }
  }, [userProfile]);

  async function fetchVendors() {
    try {
      setLoading(true);
      // Query only vendors belonging to user's organization
      const q = query(
        collection(db, 'vendors'),
        where('organizationId', '==', userProfile.organizationId),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      const vendorList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      alert('Failed to load vendors: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  function handleAddVendor() {
    setEditingVendor(null);
    setFormData({
      name: '',
      email: '',
      contactPerson: '',
      phone: '',
      address: '',
      specialization: '',
      rating: 3
    });
    setShowModal(true);
  }

  function handleEditVendor(vendor) {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      email: vendor.email || '',
      contactPerson: vendor.contactPerson || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      specialization: Array.isArray(vendor.specialization) 
        ? vendor.specialization.join(', ') 
        : vendor.specialization || '',
      rating: vendor.rating || 3
    });
    setShowModal(true);
  }

  async function handleDeleteVendor(vendorId) {
    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'vendors', vendorId));
      setVendors(vendors.filter(v => v.id !== vendorId));
      alert('Vendor deleted successfully');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      alert('Failed to delete vendor: ' + error.message);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Validate
    if (!formData.name || !formData.email) {
      alert('Name and email are required');
      return;
    }

    try {
      const vendorData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        contactPerson: formData.contactPerson.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        specialization: formData.specialization
          .split(',')
          .map(s => s.trim())
          .filter(s => s),
        rating: parseFloat(formData.rating) || 3,
        organizationId: userProfile.organizationId, // Add organization ID for data isolation
        updatedAt: Timestamp.now()
      };

      if (editingVendor) {
        // Update existing vendor
        await updateDoc(doc(db, 'vendors', editingVendor.id), vendorData);
        setVendors(vendors.map(v => 
          v.id === editingVendor.id ? { id: v.id, ...vendorData } : v
        ));
        alert('Vendor updated successfully');
      } else {
        // Create new vendor
        vendorData.createdAt = Timestamp.now();
        const docRef = await addDoc(collection(db, 'vendors'), vendorData);
        setVendors([...vendors, { id: docRef.id, ...vendorData }]);
        alert('Vendor created successfully');
      }

      setShowModal(false);
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Failed to save vendor: ' + error.message);
    }
  }

  function handleInputChange(field, value) {
    setFormData({ ...formData, [field]: value });
  }

  return (
    <div className="vendor-management-container">
      {/* Header */}
      <div className="vendor-header">
        <div>
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Vendor Management</h1>
        </div>
        <button onClick={handleAddVendor} className="add-vendor-button">
          + Add Vendor
        </button>
      </div>

      {/* Vendors Table */}
      {loading ? (
        <div className="loading">Loading vendors...</div>
      ) : vendors.length === 0 ? (
        <div className="empty-state">
          <p>No vendors found</p>
          <button onClick={handleAddVendor}>Add Your First Vendor</button>
        </div>
      ) : (
        <div className="vendors-table-container">
          <table className="vendors-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Specialization</th>
                <th>Rating</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(vendor => (
                <tr key={vendor.id}>
                  <td>{vendor.name}</td>
                  <td>{vendor.email}</td>
                  <td>{vendor.contactPerson || '-'}</td>
                  <td>{vendor.phone || '-'}</td>
                  <td>
                    {Array.isArray(vendor.specialization) 
                      ? vendor.specialization.join(', ') 
                      : vendor.specialization || '-'}
                  </td>
                  <td>
                    <span className="rating">
                      {'⭐'.repeat(Math.round(vendor.rating || 0))}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => handleEditVendor(vendor)}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteVendor(vendor.id)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={e => handleInputChange('contactPerson', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={formData.address}
                  onChange={e => handleInputChange('address', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Specialization (comma-separated)</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={e => handleInputChange('specialization', e.target.value)}
                  placeholder="e.g., Electrical, HVAC, Plumbing"
                />
              </div>

              <div className="form-group">
                <label>Rating (0-5)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.5"
                  value={formData.rating}
                  onChange={e => handleInputChange('rating', e.target.value)}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  {editingVendor ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
